import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Filter, ChevronDown, Check, Plus, X } from 'lucide-react';
import { Input } from '../ui/input';
import { FIELD_OPERATORS, operatorLabel, NO_VALUE_OPERATORS } from '../../lib/taskFilterEngine';

const styledInput = 'bg-accent/60 border border-border rounded-lg text-foreground text-sm px-3 py-1.5 focus:outline-none focus:border-amber-500/50 transition-colors placeholder:text-muted-foreground/70 w-36 max-md:w-full max-md:flex-1 max-md:min-w-[7.5rem]';

const FilterSelect = ({ value, onChange, options, placeholder, className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const selected = options.find(o => String(o.value) === String(value));
  return (
    <div ref={ref} className={`relative flex-shrink-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="flex items-center justify-between gap-2 w-full bg-accent/60 border border-border rounded-lg text-sm px-3 py-1.5 hover:border-input transition-colors"
      >
        <span className={`truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>{selected?.label ?? placeholder ?? t('ui_views.filters.select')}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 min-w-full bg-popover border border-border rounded-lg shadow-xl py-1 max-h-52 overflow-y-auto">
          {options.map(o => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors hover:bg-accent ${String(value) === String(o.value) ? 'text-amber-700 dark:text-amber-400' : 'text-foreground'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterValueInput = ({ fieldDef, condition, onChange }) => {
  const { t } = useTranslation();
  const { operator, value } = condition;
  if (!fieldDef || NO_VALUE_OPERATORS.has(operator)) return null;
  if (fieldDef.type === 'select' || fieldDef.type === 'multi_select') {
    const opts = [{ value: '', label: t('ui_views.filters.select') }, ...(fieldDef.options || [])];
    return (
      <FilterSelect value={value} onChange={v => onChange({ ...condition, value: v })} options={opts} className="w-36 max-md:flex-1 max-md:min-w-[7.5rem]" />
    );
  }
  if (fieldDef.type === 'number') {
    return <input type="number" value={value} onChange={e => onChange({ ...condition, value: e.target.value })} className={styledInput} placeholder="0" />;
  }
  if (fieldDef.type === 'date') {
    return <input type="date" value={value} onChange={e => onChange({ ...condition, value: e.target.value })} className={`${styledInput} [color-scheme:dark]`} />;
  }
  return <input type="text" value={value} onChange={e => onChange({ ...condition, value: e.target.value })} className={styledInput} placeholder={t('ui_views.filters.value')} />;
};

const FilterCondition = ({ condition, filterableFields, onChange, onRemove }) => {
  const { t } = useTranslation();
  const fieldDef = filterableFields.find(f => f.key === condition.field);
  const fieldType = fieldDef?.type ?? 'select';
  const operators = FIELD_OPERATORS[fieldType] ?? FIELD_OPERATORS.select;
  const handleFieldChange = (newField) => {
    const newDef = filterableFields.find(f => f.key === newField);
    const newType = newDef?.type ?? 'select';
    const newOps = FIELD_OPERATORS[newType] ?? FIELD_OPERATORS.select;
    onChange({ ...condition, field: newField, operator: newOps[0], value: '' });
  };
  const fieldOptions = filterableFields.map(f => ({ value: f.key, label: f.label }));
  const operatorOptions = operators.map(op => ({ value: op, label: operatorLabel(op, t) }));
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <FilterSelect value={condition.field} onChange={handleFieldChange} options={fieldOptions} className="w-36 max-md:flex-1 max-md:min-w-[7.5rem]" />
      <FilterSelect value={condition.operator} onChange={newOp => onChange({ ...condition, operator: newOp, value: '' })} options={operatorOptions} className="w-32 max-md:flex-1 max-md:min-w-[7rem]" />
      <FilterValueInput fieldDef={fieldDef} condition={condition} onChange={onChange} />
      <button onClick={onRemove} className="text-muted-foreground/70 hover:text-red-400 transition-colors flex-shrink-0"><X className="w-3 h-3" /></button>
    </div>
  );
};

const FilterGroupRow = ({ group, filterableFields, onChangeCondition, onAddOr, onRemoveCondition, onRemoveGroup }) => {
  const { t } = useTranslation();
  return (
  <div className="flex items-start gap-2 flex-wrap">
    <div className="flex items-start gap-2 flex-wrap flex-1 min-w-0">
      {group.conditions.map((cond, i) => (
        <React.Fragment key={cond.id}>
          {i > 0 && <span className="text-xs text-amber-500 font-semibold self-center px-1 py-0.5 bg-amber-500/10 rounded">{t('ui_views.filters.or')}</span>}
          <FilterCondition
            condition={cond}
            filterableFields={filterableFields}
            onChange={updated => onChangeCondition(cond.id, updated)}
            onRemove={() => onRemoveCondition(cond.id)}
          />
        </React.Fragment>
      ))}
      <button onClick={onAddOr} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-amber-700 dark:hover:text-amber-400 transition-colors self-center px-1.5 py-0.5 rounded border border-dashed border-border hover:border-amber-500/50">
        <Plus className="w-3 h-3" />{t('ui_views.filters.or')}
      </button>
    </div>
    <button onClick={onRemoveGroup} className="text-muted-foreground/70 hover:text-red-400 transition-colors flex-shrink-0 self-center mt-0.5" title={t('ui_views.filters.remove_group')}><X className="w-3.5 h-3.5" /></button>
  </div>
  );
};

/**
 * Shared search + filter bar used by every task view (List, Board, Gallery,
 * Timeline, Table). Spread the object returned by useTaskFilters into it.
 * `toolbarSlotRef` (a callback ref) marks where a view may put its own controls
 * in the same row, e.g. TasksTable's grouping and columns (its `toolbarTarget`).
 */
export default function TaskFilterBar(props) {
  const {
    searchQuery, setSearchQuery,
    filterGroups, filterableFields,
    addGroup, addOrToGroup, updateCondition, removeCondition, removeGroup, clearFilters,
    showFilterPanel, setShowFilterPanel,
    showPresetMenu, setShowPresetMenu,
    presets, isPresetActive, applyPreset,
    hasActiveFilters,
    toolbarSlotRef,
  } = props;
  const { t } = useTranslation();
  const presetMenuRef = React.useRef(null);

  React.useEffect(() => {
    if (!showPresetMenu) return;
    const close = (e) => { if (presetMenuRef.current && !presetMenuRef.current.contains(e.target)) setShowPresetMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showPresetMenu, setShowPresetMenu]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        {/* Search */}
        <div className="relative flex-1 min-w-[140px] max-w-[240px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
          <Input
            placeholder={t('tasks.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9 md:h-8 rounded-lg text-[13px] md:text-xs bg-transparent border-border"
          />
        </div>

        {/* Filter split button */}
        <div className="relative flex items-center" ref={presetMenuRef}>
          <button
            onClick={() => {
              const next = !showFilterPanel;
              setShowFilterPanel(next);
              if (next && filterGroups.length === 0) addGroup();
            }}
            title={t('ui_views.filters.filters')}
            className={`h-9 md:h-8 px-2.5 flex items-center gap-1.5 rounded-l-md border text-xs transition-colors ${showFilterPanel || hasActiveFilters ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
          >
            <Filter className="w-3.5 h-3.5" />
            {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
          </button>
          <button
            onClick={() => setShowPresetMenu(v => !v)}
            title={t('tasks.filters.presets')}
            aria-haspopup="menu"
            aria-expanded={showPresetMenu}
            className={`h-9 md:h-8 px-1 flex items-center rounded-r-md border border-l-0 text-xs transition-colors ${showPresetMenu || hasActiveFilters ? 'text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/5' : 'text-muted-foreground border-border hover:text-foreground'}`}
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPresetMenu ? 'rotate-180' : ''}`} />
          </button>
          {showPresetMenu && (
            <div className="absolute top-full mt-1 left-0 min-w-[190px] bg-popover border border-border rounded-lg shadow-xl py-1 z-50">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">{t('tasks.filters.presets')}</div>
              {presets.map(preset => {
                const active = isPresetActive(preset);
                return (
                  <button
                    key={preset.key}
                    onClick={() => applyPreset(preset)}
                    aria-pressed={active}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm transition-colors hover:bg-accent ${active ? 'text-amber-700 dark:text-amber-400' : 'text-foreground'}`}
                  >
                    {preset.label}
                    {active && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                  </button>
                );
              })}
              {hasActiveFilters && (
                <>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => { clearFilters(); setShowPresetMenu(false); }}
                    className="w-full text-left px-3 py-1.5 text-sm text-muted-foreground hover:text-red-400 hover:bg-accent transition-colors"
                  >
                    {t('common.clear_all')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {toolbarSlotRef && <div ref={toolbarSlotRef} className="flex items-center" />}
      </div>

      {/* Expanded filter panel */}
      {showFilterPanel && (
        <div className="p-4 bg-accent/60 rounded-xl border border-border space-y-2">
          {filterGroups.map((group, idx) => (
            <React.Fragment key={group.id}>
              {idx > 0 && (
                <div className="flex items-center gap-2 py-1">
                  <div className="h-px flex-1 bg-muted" />
                  <span className="text-xs text-muted-foreground font-medium tracking-wider">{t('ui_views.filters.and')}</span>
                  <div className="h-px flex-1 bg-muted" />
                </div>
              )}
              <FilterGroupRow
                group={group}
                filterableFields={filterableFields}
                onChangeCondition={(condId, updated) => updateCondition(group.id, condId, updated)}
                onAddOr={() => addOrToGroup(group.id)}
                onRemoveCondition={(condId) => removeCondition(group.id, condId)}
                onRemoveGroup={() => removeGroup(group.id)}
              />
            </React.Fragment>
          ))}
          <div className="flex items-center justify-between pt-1">
            <button onClick={addGroup} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-amber-700 dark:hover:text-amber-400 transition-colors">
              <Plus className="w-3 h-3" />{t('ui_views.filters.and')}
            </button>
            {filterGroups.length > 0 && (
              <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-red-400 transition-colors">{t('common.clear_all')}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
